/**
 * Organization boundary — the §5.8 hard boundary between organizations
 * (CC-V35-BANK-PROFILE-01; accepted design §5.8/§5.9; D-095 correction 2;
 * D-099 COR-2/COR-3).
 *
 * Compass derives the organization from the JWT, so "switching organization"
 * means acquiring a token whose org claim differs from the one currently
 * held. Every acquisition path funnels through AuthContext's
 * `saveAuthToStorage`, which calls `crossOrganizationBoundary` BEFORE the new
 * session snapshot is written. Crossing:
 *
 *  1. advances the organization epoch (`apiCache`), making every cached
 *     organization-scoped GET UNREACHABLE rather than merely stale;
 *  2. clears every organization-scoped localStorage key and prefix from the
 *     census below — localStorage is never a profile input, only a thing to
 *     be cleared at the boundary;
 *  3. leaves the access token alone: token replacement is the auth flow's
 *     job and has already happened by the time the boundary is crossed.
 *
 * PRIOR-ORGANIZATION SEMANTICS (D-099 COR-2). The previously held
 * organization is initialized lazily from the persisted session snapshot
 * (`compass_user`), NOT assumed absent: a cold page load with stale Org-A
 * storage followed by an Org-B acquisition is a SWITCH and clears the stale
 * state. Only a genuinely priorless entry (nothing persisted) is an
 * establishment, which records the organization and clears nothing.
 *
 * ============================================================================
 * localStorage TENANCY CENSUS (D-099 COR-3 — complete as of 0d80b4e)
 * ============================================================================
 * ORGANIZATION-SCOPED (cleared at the boundary):
 *  - `compass_user`             — session snapshot incl. organization
 *                                 (AuthContext). Exact key, listed below.
 *  - `bv-calculator-${journeyId}` — Business Value Calculator drafts keyed by
 *                                 journey; journeys are organization data
 *                                 (BusinessValueCalculator.tsx). Prefix.
 * PRESERVED (device/session-global, carries no organization data):
 *  - `compass_access_token`     — the credential itself; replaced by the auth
 *                                 flow before the boundary is crossed.
 *  - `fair_compass_cookie_consent` — legal consent, per human/device
 *                                 (CookieConsentBanner.tsx).
 *  - `faircompass_ai_preferences` — DISPLAY preferences for literature
 *                                 citations (booleans/limits only — verified
 *                                 in AIPreferences.tsx `AIPreferencesState`;
 *                                 no org data, no credentials, no provider
 *                                 keys). Classified device-scoped.
 *  - `fairy_welcome_dismissed`  — one-time product-tour dismissal flag
 *                                 (FairyWelcomeModal.tsx / JourneyDashboard);
 *                                 boolean UX state per human, not per org.
 * Read-only consumers of the session keys (PolicyEditorModal,
 * useGovernanceActions, Profile.tsx) hold no keys of their own.
 *
 * Surfaces that add organization-scoped storage later MUST register an exact
 * key (`registerOrgScopedStorageKey`) or a prefix
 * (`registerOrgScopedStoragePrefix`) so they inherit the clearing property
 * by construction.
 */

import { advanceOrganizationEpoch } from './apiCache';

const orgScopedStorageKeys = new Set<string>(['compass_user']);
const orgScopedStoragePrefixes = new Set<string>(['bv-calculator-']);

/**
 * `undefined` = not yet initialized this JS session (the persisted snapshot
 * decides the prior organization); `null` = initialized, no prior org.
 */
let currentOrgId: string | null | undefined = undefined;

function persistedOrganizationId(): string | null {
  try {
    const raw = localStorage.getItem('compass_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { organization?: { id?: unknown } };
    const id = parsed?.organization?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

export function registerOrgScopedStorageKey(key: string): void {
  orgScopedStorageKeys.add(key);
}

export function registerOrgScopedStoragePrefix(prefix: string): void {
  orgScopedStoragePrefixes.add(prefix);
}

function clearOrganizationScopedStorage(): void {
  try {
    for (const key of orgScopedStorageKeys) {
      localStorage.removeItem(key);
    }
    const prefixed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null) continue;
      for (const prefix of orgScopedStoragePrefixes) {
        if (key.startsWith(prefix)) {
          prefixed.push(key);
          break;
        }
      }
    }
    for (const key of prefixed) {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable (private mode / SSR) — the epoch advance still
    // isolates the in-memory cache, which is the load-bearing half.
  }
}

export function crossOrganizationBoundary(newOrgId: string): void {
  if (currentOrgId === undefined) {
    // Lazy prior-org initialization (COR-2): stale persisted state from a
    // previous page load IS a prior organization.
    currentOrgId = persistedOrganizationId();
  }
  if (currentOrgId === newOrgId) {
    return;
  }
  if (currentOrgId === null) {
    // Genuine first entry — nothing persisted anywhere. Establish silently:
    // entering an organization is not a switch, and clearing here would wipe
    // the session the auth flow is in the middle of storing.
    currentOrgId = newOrgId;
    return;
  }

  currentOrgId = newOrgId;
  advanceOrganizationEpoch();
  clearOrganizationScopedStorage();
}
